import { useState, type FormEvent } from "react";
import SectionHead from "./SectionHead";

const FORM_URL = "https://readdy.ai/api/form/dadvlbbep053ijub1620";

const channelOptions = ["校园瓜", "职场那些事", "追星现场", "邻里奇闻", "恋爱八卦", "社会热闻"];

const initialForm = {
  nickname: "",
  channel: "校园瓜",
  title: "",
  content: "",
};

export default function SubmitForm() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const update = (key: keyof typeof initialForm, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("idle");
    setFormError("");

    const fd = new FormData(e.currentTarget);
    // 蜜罐字段：非空则假装提交成功，拦截机器人
    const honeypot = (fd.get("company_alt") as string)?.trim() || "";
    if (honeypot) {
      setStatus("success");
      return;
    }

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      if (res.ok && parsed?.code === "OK" && !serverMsg.includes("spam") && !responseText.includes("spam")) {
        setStatus("success");
        setForm(initialForm);
      } else {
        setStatus("error");
        setFormError(serverMsg || "投稿提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查连接后重试。");
    }
  }

  const inputCls =
    "w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200";

  return (
    <section id="submit" className="relative bg-background-50 py-16 md:py-24" aria-label="投稿入口">
      <div className="mx-auto max-w-3xl px-4 md:px-6">
        <SectionHead
          eyebrow="投稿入口"
          title="现在就把故事投进来"
          desc="填好下面几项信息，点击提交，剩下的交给吧友审核团。图文并茂、证据清晰的内容最快上首页。"
        />

        <div className="overflow-hidden rounded-3xl border border-background-200 bg-background-100">
          {/* 顶部提示条 */}
          <div className="flex items-center gap-2 bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950">
            <i className="ri-account-circle-line text-lg" aria-hidden="true"></i>
            本投稿全程匿名，请勿在内容中暴露个人真实信息
          </div>

          <form
            id="baoliao-form"
            className="flex flex-col gap-5 p-6 md:p-8"
            onSubmit={handleSubmit}
            data-readdy-form={FORM_URL}
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="nickname" className="mb-1.5 block text-sm font-semibold text-foreground-950">
                  匿名昵称
                </label>
                <input
                  id="nickname"
                  name="nickname"
                  type="text"
                  value={form.nickname}
                  onChange={(e) => update("nickname", e.target.value)}
                  className={inputCls}
                  placeholder="如：爱吃瓜的小机灵"
                  maxLength={20}
                />
              </div>
              <div>
                <label htmlFor="channel" className="mb-1.5 block text-sm font-semibold text-foreground-950">
                  投稿频道
                </label>
                <select
                  id="channel"
                  name="channel"
                  value={form.channel}
                  onChange={(e) => update("channel", e.target.value)}
                  className={inputCls}
                >
                  {channelOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="title" className="mb-1.5 block text-sm font-semibold text-foreground-950">
                爆料标题 <span className="text-primary-500">*</span>
              </label>
              <input
                id="title"
                name="title"
                type="text"
                required
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                className={inputCls}
                placeholder="一句话点出核心瓜点，20 字内最佳"
                maxLength={30}
              />
            </div>

            <div>
              <label htmlFor="content" className="mb-1.5 block text-sm font-semibold text-foreground-950">
                详细内容 <span className="text-primary-500">*</span>
              </label>
              <textarea
                id="content"
                name="content"
                required
                rows={6}
                value={form.content}
                onChange={(e) => update("content", e.target.value)}
                className={`${inputCls} resize-none`}
                placeholder="背景铺垫 → 核心事件 → 个人看法，讲清楚前因后果，方便吧友快速吃瓜。最多 500 字。"
                maxLength={500}
              ></textarea>
              <p className="mt-1 text-right text-xs text-foreground-500">{form.content.length}/500</p>
            </div>

            {/* 蜜罐反垃圾字段 */}
            <div className="hp-guard" aria-hidden="true">
              <label htmlFor="company_alt">请不要填写此字段</label>
              <input
                type="text"
                id="company_alt"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                readOnly
              />
            </div>

            <div className="flex items-center gap-2 rounded-xl bg-secondary-100 px-4 py-3 text-xs leading-relaxed text-secondary-900">
              <i className="ri-information-line shrink-0 text-base" aria-hidden="true"></i>
              投稿即代表你承诺内容真实、基于个人经历，并同意平台对违规、造谣内容进行审核下架处理。
            </div>

            {status === "error" && (
              <p className="rounded-lg bg-primary-50 px-4 py-3 text-sm text-primary-800" role="alert">
                {formError || "投稿提交失败，请稍后再试。"}
              </p>
            )}

            <button
              type="submit"
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-4 text-base font-bold text-white transition-all hover:bg-primary-600"
            >
              <i className="ri-send-plane-fill text-lg" aria-hidden="true"></i>
              提交投稿 · 等审核
            </button>

            {status === "success" && (
              <div className="flex items-center gap-3 rounded-2xl bg-accent-500/20 px-5 py-4" role="status">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
                  <i className="ri-check-line text-xl" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-sm font-bold text-foreground-950">投稿成功啦！</p>
                  <p className="text-xs text-foreground-700">内容已进入吧友审核队列，过审后会尽快上首页，请留意。</p>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}